import { Component, forwardRef, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { ReportTemplateVar } from '../../../../../models/ReportTemplate';
import { TooltipDirective } from '../../../../../utils/tooltip.directive';
import { Editor } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';
import { TiptapEditorDirective } from 'ngx-tiptap';
import StarterKit from '@tiptap/starter-kit';

import { BackgroundColor, FontSize, TextStyle } from '@tiptap/extension-text-style';
import { FontFamily } from '@tiptap/extension-font-family';
import { Color } from '@tiptap/extension-color';
import { TextAlign } from '@tiptap/extension-text-align';
import { Placeholder } from '@tiptap/extension-placeholder';
import { ReportImage } from './report-image';

import { TableRow } from '@tiptap/extension-table-row';
import { ReportTable, ReportTableCell, ReportTableHeader, TABLE_LAYOUTS } from './report-table';
import { CommonModule } from '@angular/common';
import { VariableExtension } from './variable.extension';
import { IfBlock } from './IfBlock';
import { IfInline } from './IfInline';
@Component({
  selector: 'app-report-editor',
  imports: [
    CommonModule,
    TiptapEditorDirective,
    TooltipDirective
  ],
  templateUrl: './report-editor.html',
  styleUrl: './report-editor.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ReportEditor),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ReportEditor),
      multi: true
    }
  ]
})
export class ReportEditor implements OnInit, OnChanges, OnDestroy, ControlValueAccessor, Validator {

  editor!: Editor;

  // Variabili del BE nel formato "|PLATE|": nel report v2 diventano "{{PLATE}}"
  variableList: { id: string; name: string }[] = [];

  @Input() minHeight: string = '300px';

  @Input() required: boolean = false;

  @Input() maxImageKb: number = 200;

  tableLayouts = TABLE_LAYOUTS;

  imageError: string = '';

  showHtml: boolean = false;
  htmlOutput: string = '';

  @Input() set variables(variables: ReportTemplateVar[] | null | undefined) {
    this.variableList = (variables ?? []).map(v => ({
      id: (v.id ?? '').replace(/\|/g, ''),
      name: v.name ?? v.id ?? ''
    }));
  }

  private pendingContent: string = '';
  private pendingDisabled: boolean = false;
  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };
  private onValidatorChange: () => void = () => { };

  ngOnChanges(): void {
    this.onValidatorChange();
  }

  validate(_: AbstractControl): ValidationErrors | null {
    if (!this.required) {
      return null;
    }
    const empty = this.editor ? this.editor.isEmpty : (this.pendingContent.trim() === '' || this.pendingContent === '<p></p>');
    return empty ? { required: true } : null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  writeValue(value: string | null): void {
    this.pendingContent = value ?? '';
    if (this.editor && this.editor.getHTML() !== this.pendingContent) {
      this.editor.commands.setContent(this.pendingContent, { emitUpdate: false });
      this.htmlOutput = this.editor.getHTML();
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.pendingDisabled = isDisabled;
    this.editor?.setEditable(!isDisabled);
  }

  private getVariableOptions(): { id: string; name: string }[] {
    return this.variableList.map(v => ({ ...v }));
  }

  ngOnInit(): void {

    this.editor = new Editor({
      extensions: [
        StarterKit,
        TextStyle,
        FontFamily,
        FontSize,
        Color,
        BackgroundColor,
        ReportImage.configure({
          allowBase64: true
        }),
        TextAlign.configure({
          types: ['heading', 'paragraph']
        }),

        // BulletList,
        // OrderedList,
        // ListItem,
        ReportTable.configure({
          resizable: false,
        }),
        TableRow,
        ReportTableCell,
        ReportTableHeader,


        Placeholder.configure({
          placeholder: 'Scrivi il report...'
        }),
        VariableExtension.configure({
          getVariables: () => this.getVariableOptions()
        }),
        IfBlock.configure({
          getVariables: () => this.getVariableOptions()
        }),
        IfInline.configure({
          getVariables: () => this.getVariableOptions()
        })

      ],

      content: this.pendingContent,
      editable: !this.pendingDisabled,
      editorProps: {
        // Dentro un SE in linea l'incollato diventa testo su una riga: le righe multiple spezzerebbero il nodo
        handlePaste: (view, _event, slice) => {
          const { $from, $to } = view.state.selection;
          if ($from.parent.type.name !== 'ifInline' && $to.parent.type.name !== 'ifInline') {
            return false;
          }
          const text = slice.content.textBetween(0, slice.content.size, ' ').replace(/\s+/g, ' ').trim();
          if (!text) {
            return false;
          }
          view.dispatch(view.state.tr.insertText(text).scrollIntoView());
          return true;
        }
      },
      onUpdate: ({ editor }) => {
        this.htmlOutput = editor.getHTML();
        this.onChange(this.htmlOutput);
      },
      onBlur: () => this.onTouched()
    });
  }

  ngOnDestroy(): void {
    this.editor?.destroy();
  }

  setColor(color: string): void {
    this.editor.chain().focus().setColor(color).run();
  }
  clearColor(): void {
    this.editor.chain().focus().unsetColor().run();
  }
  setBackgroundColor(color: string): void {
    this.editor.chain().focus().setBackgroundColor(color).run();
  }
  clearBackgroundColor(): void {
    this.editor.chain().focus().unsetBackgroundColor().run();
  }
  setFontFamily(font: string): void {
    this.editor.chain().focus().setMark('textStyle', {
      fontFamily: font
    }).run();
  }
  setFontSize(size: string): void {
    this.editor
      .chain()
      .focus()
      .setMark('textStyle', {
        fontSize: size
      })
      .run();
  }
  toggleHtml(): void {
    this.showHtml = !this.showHtml;
    if (this.showHtml) {
      this.htmlOutput = this.editor.getHTML();
    }
  }

  copyHtml(): void {
    navigator.clipboard?.writeText(this.htmlOutput);
  }

  insertVariable(select: HTMLSelectElement): void {
    if (!select.value) {
      return;
    }
    this.editor.chain().focus().insertContent(`{{${select.value}}}`).run();
    select.value = '';
  }
  onFileSelected(event: Event): void {

    const input = event.target as HTMLInputElement;

    if (!input.files?.length) {
      return;
    }

    const file = input.files[0];

    if (file.size > this.maxImageKb * 1024) {
      this.imageError = `L'immagine "${file.name}" pesa ${Math.round(file.size / 1024)} KB: il limite è ${this.maxImageKb} KB.`;
      input.value = '';
      return;
    }
    this.imageError = '';

    const reader = new FileReader();

    reader.onload = () => {

      const base64 = reader.result as string;

      this.editor
        .chain()
        .focus()
        .setImage({
          src: base64
        })
        .run();
    };

    reader.readAsDataURL(file);

    input.value = '';
  }
  insertLayoutTable(select: HTMLSelectElement): void {
    const layout = this.tableLayouts.find(l => l.value === select.value);
    select.value = '';
    if (!layout) {
      return;
    }

    this.editor
      .chain()
      .focus()
      .insertContent({
        type: 'table',
        attrs: { bordered: false },
        content: [
          {
            type: 'tableRow',
            content: layout.widths.map(width => ({
              type: 'tableCell',
              attrs: { width, verticalAlign: 'top' },
              content: [{ type: 'paragraph' }]
            }))
          }
        ]
      })
      .run();
  }

  // Attiva/disattiva i bordi della tabella in cui si trova il cursore
  toggleTableBorders(): void {
    this.editor
      .chain()
      .focus()
      .command(({ tr, state }) => {
        const { $from } = state.selection;
        for (let depth = $from.depth; depth > 0; depth--) {
          const node = $from.node(depth);
          if (node.type.name === 'table') {
            tr.setNodeMarkup($from.before(depth), undefined, { ...node.attrs, bordered: !node.attrs['bordered'] });
            return true;
          }
        }
        return false;
      })
      .run();
  }

  // Allineamento verticale (top|middle|bottom) di tutte le celle delle righe selezionate
  setRowVerticalAlign(select: HTMLSelectElement): void {
    const value = select.value;
    select.value = '';
    if (!value) {
      return;
    }

    this.editor
      .chain()
      .focus()
      .command(({ tr, state }) => {
        const rowRange = ($pos: any): { start: number; end: number } | null => {
          for (let depth = $pos.depth; depth > 0; depth--) {
            if ($pos.node(depth).type.name === 'tableRow') {
              return { start: $pos.before(depth), end: $pos.after(depth) };
            }
          }
          return null;
        };

        const first = rowRange(state.selection.$from);
        const last = rowRange(state.selection.$to);
        if (!first || !last) {
          return false;
        }

        tr.doc.nodesBetween(first.start, last.end, (node, pos) => {
          if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, verticalAlign: value });
          }
        });
        return true;
      })
      .run();
  }

  addRowAfter(): void {
    this.editor.chain().focus().addRowAfter().run();
  }

  addColumnAfter(): void {
    this.editor.chain().focus().addColumnAfter().run();
  }

  deleteRow(): void {
    this.editor.chain().focus().deleteRow().run();
  }

  deleteColumn(): void {
    this.editor.chain().focus().deleteColumn().run();
  }

  // Toglie il blocco SE in cui si trova il cursore, mantenendone il contenuto
  removeIfBlock(): void {
    this.editor
      .chain()
      .focus()
      .command(({ tr, state }) => {
        const { $from } = state.selection;
        for (let depth = $from.depth; depth > 0; depth--) {
          const node = $from.node(depth);
          if (node.type.name === 'ifBlock' || node.type.name === 'ifInline') {
            const pos = $from.before(depth);
            tr.replaceWith(pos, pos + node.nodeSize, node.content);
            return true;
          }
        }
        return false;
      })
      .run();
  }

  // Avvolge la selezione (stesso blocco di testo) in un SE in linea, mantenendo grassetti e stili
  insertIfInline(): void {
    this.editor
      .chain()
      .focus()
      .command(({ tr, state }) => {
        const { from, to, $from, $to } = state.selection;
        if (!$from.sameParent($to) || !$from.parent.isTextblock) {
          return false;
        }
        const content = from === to
          ? state.schema.text(' ')
          : state.doc.slice(from, to).content;
        const node = state.schema.nodes['ifInline'].create(
          { field: this.variableList[0]?.id ?? '', operator: '==', value: '' },
          content
        );
        tr.replaceWith(from, to, node);
        // Cursore dentro il SE: senza, incolla/digitazione immediata finiscono fuori dal nodo
        const start = from + 1;
        tr.setSelection(
          from === to
            ? TextSelection.create(tr.doc, start, start + 1)
            : TextSelection.create(tr.doc, from + node.nodeSize - 1)
        );
        return true;
      })
      .run();
  }

  insertIfBlock(): void {

    this.editor
      .chain()
      .focus()
      .insertContent({
        type: 'ifBlock',
        attrs: {
          field: this.variableList[0]?.id ?? '',
          operator: '==',
          value: ''
        },
        content: [
          {
            type: 'paragraph',
            content: [
              { type: 'text', text: ' ' }
            ]
          }
        ]
      })
      .run();
  }
}